<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
  watchEffect,
} from "vue";
import { useRoute } from "vue-router";

import HistogramPlot from "@/components/datascience/HistogramPlot.vue";
import ReportMetrics from "@/components/datascience/ReportMetrics.vue";
import TableReport, { type TableReportDto } from "@/components/datascience/TableReport.vue";
import ColorBadge from "@/components/ui/ColorBadge.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import SlideToggle from "@/components/ui/SlideToggle.vue";
import {
  MediaType,
  type CrossValidationReportDto,
  type EstimatorReportDto,
  type ReportMediaDto,
} from "@/dto";
import TwoColumnsLayout from "@/layouts/TwoColumnsLayout.vue";
import type { InspectionDataframe } from "@/services/inspectionHelpers";
import { useDatasourceStore } from "@/stores/datasource";
import CrossValidationReportViewSplitSelector from "@/views/reports/CrossValidationReportViewSplitSelector.vue";
import ReportModelPipeline from "@/views/reports/ReportModelPipeline.vue";
import ReportSummary from "@/views/reports/ReportSummary.vue";
import { useReportScrolling } from "@/views/reports/useReportScrolling";

import { splitting_strategy } from "@/assets/i18n/en.json";
import CurvePlot from "@/components/datascience/CurvePlot.vue";
import { intervalMaxMinRatio } from "@/services/utils";

const { report, estimators } = defineProps<{
  report: CrossValidationReportDto;
  estimators: Map<number, EstimatorReportDto>;
  useAnchor?: boolean;
}>();

const emit = defineEmits<{
  openEstimator: [reportId: number];
}>();

const route = useRoute();
const datasource = useDatasourceStore();

const inspectionDataframes = shallowRef<InspectionDataframe[]>([]);
const selectedSplitIndex = ref(-1);
const tableReportSpec = ref<TableReportDto | null>(null);
const splitSelectorCard = useTemplateRef<InstanceType<typeof ExpandableCard>>("splitSelectorCard");
const areTargetDistributionCombined = ref(false);

const tableReport = useTemplateRef<InstanceType<typeof TableReport>>("tableReport");
const twoColumnsLayout = useTemplateRef<InstanceType<typeof TwoColumnsLayout>>("twoColumnsLayout");
const reportMetrics = useTemplateRef<InstanceType<typeof ReportMetrics>>("reportMetrics");
const reportModelPipeline =
  useTemplateRef<InstanceType<typeof ReportModelPipeline>>("reportModelPipeline");

// Set up scrolling functionality
const { scrollToAnchor } = useReportScrolling({
  twoColumnsLayoutRef: twoColumnsLayout,
  tableReportRef: tableReport,
  reportMetricsRef: reportMetrics,
  reportModelPipelineRef: reportModelPipeline,
});

const selectedReport = computed(() => {
  const selectedEstimatorId = report.estimators[selectedSplitIndex.value];
  return estimators.get(selectedEstimatorId ?? -1);
});

const tableReportMedia = computed(() => {
  const medias = selectedReport.value?.medias ?? report.medias;
  const reports = medias.filter(
    (m) => m.category === "data" && m.content_type === MediaType.TableReport
  );
  return reports.length > 1
    ? reports.find((r) => r.data_source === datasource.sourceName)
    : reports[0];
});

const inspectionMediasForMenu = computed(() => {
  const seen = new Set<string>();
  const out: ReportMediaDto[] = [];
  for (const m of report.medias ?? []) {
    if (m.category !== "inspection" || m.method == null || seen.has(m.method)) continue;
    seen.add(m.method);
    out.push(m);
  }
  return out;
});

const targetDistributionTest = computed(() => {
  const split = report.splitting_strategy.splits[selectedSplitIndex.value];
  if (!split) {
    return [];
  }

  return split.test.target_distribution ?? [];
});

const targetDistributionTestLabels = computed(() => {
  return _targetDistributionLabels(targetDistributionTest.value);
});

const targetDistributionTrain = computed(() => {
  const split = report.splitting_strategy.splits[selectedSplitIndex.value];
  if (!split) {
    return [];
  }

  return split.train.target_distribution ?? [];
});

const targetDistributionTrainLabels = computed(() => {
  return _targetDistributionLabels(targetDistributionTrain.value);
});

function _targetDistributionLabels(distribution: number[] | null) {
  if (!distribution) {
    return [];
  }

  const sampleCount = areTargetDistributionCombined.value
    ? report.dataset_size
    : distribution.reduce((acc, c) => acc + c, 0);
  return distribution.map((c) => `Proportion: ${((c * 100) / sampleCount).toFixed(1)}%`);
}

const combinedTargetDistribution = computed(() => {
  const split = report.splitting_strategy.splits[selectedSplitIndex.value];
  if (!split) {
    return [];
  }

  if (report.ml_task.includes("classification")) {
    const length = Math.min(
      targetDistributionTrain.value.length,
      targetDistributionTest.value.length
    );
    const zipped: number[][] = Array.from({ length }, (_, i) => [
      (targetDistributionTrain.value[i] ?? 0) / split.train.sample_count,
      (targetDistributionTest.value[i] ?? 0) / split.test.sample_count,
    ]);
    return zipped;
  } else {
    return [targetDistributionTrain.value, targetDistributionTest.value];
  }
});

const combinedTargetDistributionLabels = computed(() => {
  return [
    targetDistributionTrain.value.map((c) => `Count: ${c}`),
    targetDistributionTest.value.map((c) => `Count: ${c}`),
  ];
});

const targetRangeTickFormat = computed(() => {
  if (!report.target_range) {
    return ".0f";
  }

  const sorted = [...report.target_range].sort((a, b) => a - b);
  const r = (sorted[1] ?? 0) - (sorted[0] ?? 0);
  return r > 5 ? ".0e" : ".0f";
});

const testTargetDistributionsTickFormat = computed(() => {
  return _targetDistributionsTickFormat(targetDistributionTest.value);
});

const trainTargetDistributionsTickFormat = computed(() => {
  return _targetDistributionsTickFormat(targetDistributionTrain.value);
});

const combinedTargetDistributionsTickFormat = computed(() => {
  return _targetDistributionsTickFormat(combinedTargetDistribution.value.flat());
});

function _targetDistributionsTickFormat(distribution: number[]) {
  const r = intervalMaxMinRatio(distribution);
  return r > 5 ? ".0e" : ".0f";
}

async function fetchInspectionDataframes() {
  if (!report.medias?.length) {
    inspectionDataframes.value = [];
    return;
  }
  const inspectionMedias = report.medias.filter(
    (m) => m.category === "inspection" && m.content_type === MediaType.DataFrame
  );
  const promises = inspectionMedias.map(async (m) => {
    const response = await fetch(m.presigned_url);
    const representation = await response.json();
    return {
      method: m.method,
      verboseMethodName: m.verbose_name.replace("Inspection - ", ""),
      indexNames: representation.index_names,
      datasource: m.data_source,
      ...representation,
    };
  });
  inspectionDataframes.value = await Promise.all(promises);
}

function onSplitSelectorExpanded(isExpanded: boolean) {
  if (!isExpanded) {
    selectedSplitIndex.value = -1;
  } else {
    if (selectedSplitIndex.value === -1) {
      selectedSplitIndex.value = 0;
    }
  }
}

onMounted(async () => {
  await fetchInspectionDataframes();

  // Handle initial page load with anchor
  // For anchors that don't need tableReport data, scroll immediately
  // For table-report anchors, wait for tableReportSpec to be ready
  if (route.hash) {
    const hash = route.hash.replace("#", "");
    const needsTableReport = hash.startsWith("table-report-") || hash === "table-report";

    if (!needsTableReport) {
      await nextTick();
      scrollToAnchor(route.hash);
    }
  }
});

watchEffect(async () => {
  if (tableReportMedia.value) {
    const response = await fetch(tableReportMedia.value.presigned_url);
    tableReportSpec.value = (await response.json()) as TableReportDto;
  } else {
    tableReportSpec.value = null;
  }
});

// Handle initial page load with anchor for table-report after data is ready
watch(
  () => tableReportSpec.value,
  async (newValue, oldValue) => {
    // Only handle initial hash on first value assignment (when oldValue is undefined)
    if (oldValue === undefined && route.hash) {
      const hash = route.hash.replace("#", "");
      const needsTableReport = hash.startsWith("table-report-") || hash === "table-report";

      if (needsTableReport) {
        await nextTick();
        scrollToAnchor(route.hash);
      }
    }
  },
  { immediate: true }
);

watch(selectedSplitIndex, () => {
  if (selectedSplitIndex.value !== -1) {
    splitSelectorCard.value?.expand();
  } else {
    splitSelectorCard.value?.collapse();
  }
});

defineExpose({ selectedSplitIndex });
</script>

<template>
  <div class="cross-validation-report">
    <TwoColumnsLayout ref="twoColumnsLayout">
      <template #leftPrelude>
        <slot name="summaryPrelude" />
      </template>

      <template #left>
        <ReportSummary
          :report="report"
          :inspection-medias="inspectionMediasForMenu"
          v-model:selected-split-index="selectedSplitIndex"
        />
      </template>

      <template #rightPrelude>
        <slot name="contentPrelude" />
      </template>

      <template #right>
        <div class="cards">
          <div id="split-selector">
            <ExpandableCard
              ref="splitSelectorCard"
              title="Split selector"
              icon="icon-bar-chart-4"
              @expanded="onSplitSelectorExpanded"
            >
              <div class="split-selector text-sm-medium">
                <div class="strategy">
                  {{ report.splitting_strategy_name }}

                  <FloatingTooltip class="info-tooltip" placement="bottom-end">
                    <i class="icon icon-info-circle-2" />
                    <template #tooltipContent>
                      <div class="strategy-tooltip-content text-base-regular">
                        <div class="summary">
                          <div class="title">
                            <i class="icon icon-tactics" /> Cross-validation type
                          </div>

                          <div class="name text-base-medium">
                            {{ report.splitting_strategy_name }}
                          </div>

                          <div
                            v-if="
                              splitting_strategy[
                                report.splitting_strategy_name as keyof typeof splitting_strategy
                              ]
                            "
                            class="description"
                          >
                            {{
                              splitting_strategy[
                                report.splitting_strategy_name as keyof typeof splitting_strategy
                              ]
                            }}
                          </div>
                        </div>

                        <div class="current-split">
                          <div>
                            <div class="key">
                              <i class="icon icon-arrow-split" /> Number of splits
                            </div>
                            <div class="value text-base-medium">
                              {{
                                report.splitting_strategy.splits.length /
                                report.splitting_strategy.repeat_count
                              }}
                            </div>
                          </div>

                          <div>
                            <div class="key">
                              <i class="icon icon-numbered-list" /> Number of repetitions
                            </div>
                            <div class="value text-base-medium">
                              {{
                                report.splitting_strategy.repeat_count > 1
                                  ? report.splitting_strategy.repeat_count
                                  : "Not applicable"
                              }}
                            </div>
                          </div>

                          <div>
                            <div class="key"><i class="icon icon-dices" /> Random seed</div>
                            <div class="value text-base-medium">
                              {{
                                report.splitting_strategy.seed !== null
                                  ? report.splitting_strategy.seed
                                  : "Not applicable"
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </FloatingTooltip>
                </div>

                <div class="ordinate-legend">Sample index</div>

                <CrossValidationReportViewSplitSelector
                  ref="splitSelector"
                  :splits="report.splitting_strategy.splits.map((s) => s.train_test_distribution)"
                  :dataset-size="report.dataset_size"
                  v-model:selected-split-index="selectedSplitIndex"
                />
              </div>

              <template #expandableContent>
                <div class="split-details card-content">
                  <div class="details">
                    <header class="text-base-medium">
                      <i class="title-icon icon icon-bar-chart-4" />
                      Split details
                    </header>

                    <ColorBadge
                      v-if="selectedSplitIndex !== -1 && selectedReport !== undefined"
                      class="split-info"
                      color="blue"
                      size="l"
                    >
                      <i class="icon icon-info-circle-2" />
                      Access the detailed estimator report for this split:
                      <span class="split-report-link-group">
                        <i class="icon icon-test-tube" />
                        <span
                          class="split-report-link"
                          @click="emit('openEstimator', selectedReport.id)"
                        >
                          {{ selectedReport.key }}
                        </span>
                      </span>
                    </ColorBadge>
                  </div>

                  <div class="information">
                    <header class="text-base-medium">General information</header>

                    <div class="information-grid">
                      <div class="information-grid-item">
                        <div class="key">
                          <i class="icon icon-pie-chart" />
                          Train sample count
                        </div>
                        <div class="value number">
                          {{
                            report.splitting_strategy.splits[selectedSplitIndex]?.train.sample_count
                          }}
                        </div>
                      </div>

                      <div class="information-grid-item">
                        <div class="key">
                          <i class="icon icon-pie-chart" />
                          Test sample count
                        </div>
                        <div class="value number">
                          {{
                            report.splitting_strategy.splits[selectedSplitIndex]?.test.sample_count
                          }}
                        </div>
                      </div>

                      <!-- TODO: once we are supporting groups, add this information back
                      <div class="information-grid-item">
                        <div class="key">
                          <i class="icon icon-receipt" />
                          Groups in train
                        </div>
                        <div class="value">
                          {{
                            report.splitting_strategy.splits[
                              selectedSplitIndex
                            ]?.train.groups?.join(", ") ?? "Not applicable"
                          }}
                        </div>
                      </div>

                      <div class="information-grid-item">
                        <div class="key">
                          <i class="icon icon-signal" />
                          Groups in test
                        </div>
                        <div class="value">
                          {{
                            report.splitting_strategy.splits[selectedSplitIndex]?.test.groups?.join(
                              ", "
                            ) ?? "Not applicable"
                          }}
                        </div>
                      </div>
                    --></div>
                  </div>

                  <div class="target">
                    <header class="text-base-medium">
                      <div>Target</div>
                      <div class="side-by-side-toggle-label">
                        <span :class="{ selected: !areTargetDistributionCombined }">
                          Side by side
                        </span>

                        <SlideToggle v-model:is-toggled="areTargetDistributionCombined" />

                        <span :class="{ selected: areTargetDistributionCombined }">
                          Combined
                          <FloatingTooltip class="info-tooltip" placement="bottom-end">
                            <i class="icon icon-info-circle-2" />
                            <template #tooltipContent>
                              <template v-if="report.ml_task.includes('classification')">
                                For each class label, aligned bars indicate similar distributions
                                between train (blue) and test (orange) sets;
                                <br />
                                misaligned bars indicate a distribution shift for that class.
                              </template>
                              <template v-else>
                                Overlapping curves indicate similar distributions between train
                                (blue) and test (orange) sets;
                                <br />
                                separated curves indicate a distribution shift in target values.
                              </template>
                            </template>
                          </FloatingTooltip>
                        </span>
                      </div>
                    </header>

                    <div v-if="areTargetDistributionCombined" class="plots combined">
                      <div class="plot">
                        <HistogramPlot
                          v-if="report.ml_task.includes('classification')"
                          :traces="combinedTargetDistribution"
                          :trace-names="['Train', 'Test']"
                          :bins-names="report.class_names ?? undefined"
                          :bar-labels="combinedTargetDistributionLabels"
                          x-axis-tick-format=".0%"
                          x-axis-legend="Proportion"
                        />

                        <CurvePlot
                          v-else
                          :traces="combinedTargetDistribution"
                          :trace-names="['Train', 'Test']"
                          :x-range="report.target_range ?? [0, 1]"
                          :x-axis-tick-format="targetRangeTickFormat"
                          :y-axis-tick-format="combinedTargetDistributionsTickFormat"
                          x-axis-legend="Target value"
                          y-axis-legend="Density"
                        />
                      </div>
                    </div>
                    <div v-else class="plots">
                      <div class="plot">
                        <div class="legend">
                          <i class="icon icon-receipt" />
                          Train
                        </div>

                        <HistogramPlot
                          v-if="report.ml_task.includes('classification')"
                          :traces="[targetDistributionTrain]"
                          :bins-names="report.class_names ?? undefined"
                          :bar-labels="[targetDistributionTrainLabels]"
                          :margin="{ top: 40, right: 0, bottom: 15, left: 0 }"
                          x-axis-legend="Sample count"
                        />

                        <CurvePlot
                          v-else
                          :traces="[targetDistributionTrain]"
                          :x-range="report.target_range ?? [0, 1]"
                          :x-axis-tick-format="targetRangeTickFormat"
                          :y-axis-tick-format="trainTargetDistributionsTickFormat"
                          x-axis-legend="Target value"
                          y-axis-legend="Density"
                        />
                      </div>

                      <div class="plot">
                        <div class="legend">
                          <i class="icon icon-signal" />
                          Test
                        </div>

                        <HistogramPlot
                          v-if="report.ml_task.includes('classification')"
                          :traces="[targetDistributionTest]"
                          :bins-names="report.class_names ?? undefined"
                          :bar-labels="[targetDistributionTestLabels]"
                          :margin="{ top: 40, right: 0, bottom: 15, left: 0 }"
                          x-axis-legend="Sample count"
                        />

                        <CurvePlot
                          v-else
                          :traces="[targetDistributionTest]"
                          :x-range="report.target_range ?? [0, 1]"
                          :x-axis-tick-format="targetRangeTickFormat"
                          :y-axis-tick-format="testTargetDistributionsTickFormat"
                          x-axis-legend="Target value"
                          y-axis-legend="Density"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </ExpandableCard>
          </div>

          <div id="table-report">
            <TableReport v-if="tableReportSpec" ref="tableReport" :spec="tableReportSpec">
              <template #actions></template>
            </TableReport>
          </div>

          <div id="general-metrics">
            <ReportMetrics
              ref="reportMetrics"
              :report="selectedReport ?? report"
              :parent-report="report"
            />
          </div>

          <div id="pipeline">
            <ReportModelPipeline
              ref="reportModelPipeline"
              :report="report"
              :inspection-dataframes="inspectionDataframes"
            />
          </div>
        </div>
      </template>
    </TwoColumnsLayout>
  </div>
</template>

<style scoped>
.info-tooltip {
  cursor: help;
}

.strategy-tooltip-content {
  display: flex;
  max-width: 450px;
  flex-direction: column;
  padding: var(--numbers-12);
  gap: var(--numbers-24);

  .summary {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);
  }

  .title,
  .key {
    color: var(--content-brand-2);
  }

  .description {
    color: var(--content-tertiary);
  }

  .current-split {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-4);

    > div {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: var(--numbers-8);
    }

    .value {
      text-align: right;
    }
  }
}

.cross-validation-report {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  .cards {
    display: flex;
    flex-direction: column;
    padding-bottom: var(--numbers-32);
    gap: var(--numbers-32);

    .split-selector {
      position: relative;
      overflow: hidden;
      padding: var(--numbers-16) 0 0 var(--numbers-8);
      border: solid var(--border-tertiary) var(--stroke-width-md);
      border-radius: var(--numbers-16);
      margin: var(--numbers-4) 0;

      .strategy {
        position: absolute;
        top: 0;
        right: 0;
        padding: var(--numbers-8);
        border-bottom: solid var(--border-tertiary) var(--stroke-width-md);
        border-left: solid var(--border-tertiary) var(--stroke-width-md);
        background-color: var(--background-secondary);
        border-bottom-left-radius: var(--numbers-8);
        color: var(--content-tertiary);
        cursor: help;

        .icon {
          vertical-align: text-top;
        }
      }

      .ordinate-legend {
        color: var(--content-disabled);
      }
    }

    .split-details {
      display: flex;
      flex-direction: column;
      border-color: var(--border-brand);
      gap: var(--numbers-32);

      .details {
        display: flex;
        flex-direction: column;
        gap: var(--numbers-16);

        header {
          color: var(--content-brand);

          .title-icon {
            vertical-align: text-bottom;
          }
        }

        .split-info {
          flex-grow: 1;
          color: var(--content-info);
          text-align: left;

          i {
            vertical-align: middle;
          }

          .split-report-link-group {
            color: var(--content-link);
          }

          .split-report-link {
            cursor: pointer;
            text-decoration: underline;
          }
        }
      }

      .information {
        display: flex;
        flex-direction: column;
        gap: var(--numbers-16);

        header {
          color: var(--content-tertiary);
        }

        .information-grid {
          display: grid;
          overflow: hidden;
          border: solid var(--border-tertiary) var(--stroke-width-md);
          border-radius: var(--numbers-16);
          background-color: var(--border-tertiary);
          gap: var(--stroke-width-md);
          grid-template-columns: repeat(
            auto-fit,
            minmax(max(280px, calc((100% - var(--stroke-width-md)) / 2)), 1fr)
          );

          .information-grid-item {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: var(--numbers-16);
            background-color: var(--background-primary);

            .key {
              color: var(--content-tertiary);
            }

            .value {
              color: var(--content-primary);

              &.number {
                color: var(--content-brand);
              }
            }
          }
        }
      }

      .target {
        display: flex;
        flex-direction: column;
        gap: var(--numbers-16);

        header {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          color: var(--content-tertiary);

          .side-by-side-toggle-label {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: var(--numbers-8);

            > span {
              color: var(--content-tertiary);
              transition: color var(--animation-duration) var(--animation-easing);
            }

            > span.selected {
              color: var(--content-brand);
            }

            .icon {
              color: inherit;
              vertical-align: text-top;
            }
          }
        }

        .plots {
          display: grid;
          gap: var(--numbers-8);
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));

          &.combined {
            grid-template-columns: 1fr;
          }

          .plot {
            position: relative;
            overflow: hidden;
            min-height: 200px;
            padding: 0 var(--numbers-8) var(--numbers-8) var(--numbers-8);
            border: solid var(--border-tertiary) var(--stroke-width-md);
            border-radius: var(--numbers-16);

            .legend {
              position: absolute;
              top: 0;
              right: 0;
              padding: var(--numbers-8);
              border-bottom: solid var(--border-tertiary) var(--stroke-width-md);
              border-left: solid var(--border-tertiary) var(--stroke-width-md);
              background-color: var(--background-secondary);
              border-bottom-left-radius: var(--numbers-8);
              color: var(--content-tertiary);

              .icon {
                vertical-align: text-top;
              }
            }
          }
        }
      }
    }
  }
}
</style>
